/**
 * @module toasterHelpers
 *
 * Centralized helper for showing toast notifications across the app.
 * Provides simple functions for success, error, warning, and info toasts.
 *
 * @example
 * ```ts
 * import { toast } from "@/components/ui/toaster/toasterHelpers";
 *
 * toast.success("Profile saved successfully");
 * toast.error("Failed to save profile", "Network error");
 * toast.warning("Low storage space");
 * toast.info("Settings have been updated");
 * ```
 *
 * @description
 * Each method accepts a title (required) and optional description,
 * and automatically applies consistent duration, style, and type.
 */

import type { ReactNode } from 'react'
import { toaster } from './toasterInstance'

type ToastKind = 'success' | 'error' | 'warning' | 'info'

export type ToastOptions = {
  duration?: number
  closable?: boolean
  id?: string
  /** Rendered by the Toaster as the toast's action button. */
  action?: { label: string; onClick: () => void }
}

function createType(type: ToastKind) {
  return (title: string, description?: string | ReactNode, options: ToastOptions = {}): void => {
    toaster.create({
      title,
      description,
      type,
      duration: options?.duration ?? (type === 'error' ? 3000 : 2000),
      closable: options?.closable ?? true,
      id: options.id ?? Math.random().toString(36).slice(2),
      action: options.action,
    })
  }
}

export const toast = {
  success: createType('success'),
  error: createType('error'),
  warning: createType('warning'),
  info: createType('info'),
  dismiss: toaster.dismiss,
}
